/**
 * The links an owner types into the Grow settings panel (a notice's button, a
 * tip's "open a link"), read before a button is drawn for one.
 *
 * Four kinds work: a page in the app ("/plants"), which goes through the
 * router; a web address (https, or http), which opens in a new tab with
 * nothing passed to it; and an email address or a phone number (mailto:,
 * tel:), which the device's own app takes. Anything else is refused:
 * javascript: and data: above all, since the panel's text is typed by hand,
 * but also a bare "example.com" or "plants", which would go nowhere. A link
 * that is refused gets no button, rather than one that does nothing.
 *
 * Who uses it: shared/components/LinkButton, Today's TodayNotice, and the
 * tips (TipViews, useTipAction).
 *
 * @module safeLink
 */

/** A link that is safe to follow, and how. */
export type SafeLink =
  /** A page in the app: a path, its search and its #part, for the router. */
  | { kind: 'app'; to: string }
  /** A web page: opened in a new tab. */
  | { kind: 'web'; href: string }
  /** An email address or a phone number: opened where it is. */
  | { kind: 'contact'; href: string };

/** Web addresses, opened in a new tab. */
const WEB = new Set(['https:', 'http:']);
/** Email and phone, opened by the device's own app. */
const CONTACT = new Set(['mailto:', 'tel:']);

/**
 * Reads a typed link into one that is safe to follow, or null.
 *
 * A path is read against the app's own address and kept only if it stays
 * there, so "//elsewhere.com" or "/\elsewhere.com" (which a browser takes
 * for another site) is refused.
 *
 * @param raw - The link as the panel sends it.
 * @returns The link and its kind, or null when there is nothing safe to follow.
 */
export function readLink(raw: string | null | undefined): SafeLink | null {
  const text = (raw ?? '').trim();
  if (!text) return null;
  if (text.startsWith('/')) {
    const here = window.location.origin;
    let url: URL;
    try { url = new URL(text, here); } catch { return null; }
    if (url.origin !== here) return null;
    return { kind: 'app', to: `${url.pathname}${url.search}${url.hash}` };
  }
  let url: URL;
  try { url = new URL(text); } catch { return null; }
  if (WEB.has(url.protocol)) return url.hostname ? { kind: 'web', href: url.href } : null;
  // Something after the scheme: an address, a number, or a ?subject= at least.
  if (CONTACT.has(url.protocol)) return url.pathname || url.search ? { kind: 'contact', href: url.href } : null;
  return null;
}

/**
 * Follows a safe link from code, where no link can be drawn (a tip's button
 * run by the screen that shows it).
 *
 * @param link - The link, read by readLink.
 * @param navigate - The router's navigate, for a page in the app.
 */
export function followLink(link: SafeLink, navigate: (to: string) => void): void {
  if (link.kind === 'app') navigate(link.to);
  else if (link.kind === 'web') window.open(link.href, '_blank', 'noopener,noreferrer');
  else window.location.assign(link.href);
}
